import { normalizeJobTypeKey } from "./container-group-job-types";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp, toNonNegativeInteger } from "@racoon/lib";

/** Form amounts are whole tenge; the API takes them as int64. */
export const toAmount = (value?: number) =>
  value === undefined ? undefined : BigInt(Math.round(value));

/** One priced job type on a contract. */
export type JobTypeTariff = { type: string; amount: number | "" };

export function parseTariffRate(raw: unknown): JobTypeTariff[] {
  if (!raw) return [];

  let parsed: Record<string, unknown>;
  try {
    parsed = typeof raw === "string" ? JSON.parse(raw) : (raw as Record<string, unknown>);
  } catch {
    return [];
  }

  return Object.entries(parsed).map(([key, amount]) => ({
    type: normalizeJobTypeKey(key),
    amount: Math.trunc(Number(amount)) || 0,
  }));
}

/** Multiselect semantics: a type already priced is removed, any other is added unpriced. */
export function toggleJobTypeTariff(tariffs: JobTypeTariff[], tariffKey: string): JobTypeTariff[] {
  return tariffs.some((tariff) => tariff.type === tariffKey)
    ? tariffs.filter((tariff) => tariff.type !== tariffKey)
    : [...tariffs, { type: tariffKey, amount: "" }];
}

/** Whole non-negative tenge only; an emptied field stays empty rather than becoming 0. */
export function setJobTypeTariffAmount(
  tariffs: JobTypeTariff[],
  tariffKey: string,
  amount: string,
): JobTypeTariff[] {
  return tariffs.map((tariff) =>
    tariff.type === tariffKey ? { ...tariff, amount: toNonNegativeInteger(amount) } : tariff,
  );
}

/** The wire shape of the tariffs; unpriced rows are dropped, no rows means no field. */
export function buildTariffRate(tariffs: JobTypeTariff[]): Record<string, number> | undefined {
  const rate: Record<string, number> = {};
  for (const tariff of tariffs) {
    if (tariff.amount !== "") rate[tariff.type] = tariff.amount;
  }
  return Object.keys(rate).length > 0 ? rate : undefined;
}

/** The editable fields of a contract, as both contract forms hold them in state. */
export type ContractFormValues = {
  dealNumber: string;
  name: string;
  startDate?: Date;
  endDate?: Date;
  /** Rent is a checkbox plus an amount; a stored rent of 0 still counts as rented. */
  isRentBunker: boolean;
  rentCost: number | "";
  tariffs: JobTypeTariff[];
  /** Fixed at create — ContractUpdate carries no type. */
  type: ConnectProto.ContractType;
  /** BY_NORM only: the flat monthly charge that replaces tariffs. */
  monthlyFee: number | "";
};

/** Loads a stored contract into form state — the customer card and the onboarding wizard hydrate identically. */
export function contractToFormValues(contract: ConnectProto.Contract): ContractFormValues {
  const hasRent = contract.rentCost !== undefined && contract.rentCost !== null;
  return {
    dealNumber: contract.dealNumber ?? "",
    name: contract.name ?? "",
    startDate: (contract.startAt && fromTimestamp(contract.startAt)) || undefined,
    endDate: (contract.endAt && fromTimestamp(contract.endAt)) || undefined,
    isRentBunker: hasRent,
    rentCost: hasRent ? Number(contract.rentCost) : 0,
    tariffs: parseTariffRate(contract.tariffRateByJobType),
    type: contract.type || ConnectProto.ContractType.WASTE_OPERATOR,
    monthlyFee: Number(contract.monthlyFee ?? 0),
  };
}
